import { Button, CircularProgress, Dialog, DialogActions, DialogContent } from '@mui/material';
import {
  addAnomalyPayload,
  AlertSnackbar,
  AnomalyAPI,
  DialogTitle,
  getStartEndOffsets,
  Nullable,
} from '@shared';
import { useState } from 'react';
import * as S from './ReviewIdentity.styled';
import { ReviewIdentityPropsType } from './ReviewIdentity.types';
import { API_ROUTES, auditApi } from 'App';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const { REACT_APP_URL } = process.env;

export function ReviewIdentityDialog({
  auditUuid = '',
  title,
  profileTraceHandle,
  documentTraceHandle,
  roomScanTraceHandle,
  isModalOpen,
  onCloseModal,
  identityVerification,
  onApproveExamTaker,
  onNotApproveExamTaker,
  hideNotificationShowing,
  refetchAnomalies = () => null,
  showDocumentToPartner = false,
}: ReviewIdentityPropsType) {
  const [isOpenError, setIsOpenError] = useState(false);

  const queryClient = useQueryClient();

  const { mutate: addAnomaly, isLoading } = useMutation({
    mutationFn: (payload: addAnomalyPayload) => auditApi.addAnomaly(auditUuid, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['anomalies', auditUuid], (oldData: Nullable<AnomalyAPI[]>) => {
        if (oldData) {
          return [data, ...oldData];
        } else {
          refetchAnomalies();
          return [];
        }
      });
    },
  });

  const handleApproveExamTaker = () => {
    onApproveExamTaker && onApproveExamTaker();
    hideNotificationShowing && hideNotificationShowing();
    onCloseModal();
  };

  const handleNotApproveExamTaker = () => {
    onNotApproveExamTaker && onNotApproveExamTaker();
    hideNotificationShowing && hideNotificationShowing();
    onCloseModal();
  };

  const handleNoIdProvided = () => {
    addAnomaly({
      id: 28,
      ...getStartEndOffsets('No Id Provided', 0),
    });
  };

  return (
    <>
      <AlertSnackbar
        text={'Something went wrong with the review identity video'}
        open={isOpenError}
        handleClose={() => setIsOpenError(false)}
      />
      <Dialog fullWidth open={isModalOpen} onClose={onCloseModal} closeAfterTransition>
        <DialogTitle fontSize={24} title={title} onClose={onCloseModal} />
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div>
            <strong>Profile video:</strong>
            <S.Video muted controls autoPlay>
              <source
                onError={() => setIsOpenError(true)}
                src={REACT_APP_URL + API_ROUTES.apiV1AuditVideo(profileTraceHandle)}
                type='video/webm'
              />
            </S.Video>
          </div>
          {documentTraceHandle && (
            <div>
              <strong>Document video:</strong>
              <S.Video muted controls autoPlay>
                <source
                  onError={() => setIsOpenError(true)}
                  src={REACT_APP_URL + API_ROUTES.apiV1AuditVideo(documentTraceHandle)}
                  type='video/webm'
                />
              </S.Video>
            </div>
          )}
          {roomScanTraceHandle && (
            <div>
              <strong>Room scan:</strong>
              <S.Video muted controls autoPlay>
                <source
                  onError={() => setIsOpenError(true)}
                  src={REACT_APP_URL + API_ROUTES.apiV1AuditVideo(roomScanTraceHandle)}
                  type='video/webm'
                />
              </S.Video>
            </div>
          )}
        </DialogContent>
        {identityVerification && (
          <DialogActions sx={{ p: '8px 24px' }}>
            {showDocumentToPartner && (
              <Button color='inherit' variant='contained' onClick={handleNoIdProvided}>
                {isLoading ? <CircularProgress size={18} color='inherit' /> : 'No ID provided'}
              </Button>
            )}
            <Button color='error' variant='contained' onClick={handleNotApproveExamTaker}>
              No, not the exam taker
            </Button>
            <Button color='success' variant='contained' onClick={handleApproveExamTaker}>
              Yes, is the exam taker
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}
